'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { LpFooter } from './_components/LpFooter';
import {
  contactHref,
  loginHref,
  planListHref,
  samplesHref,
  templatesHref,
  trialSignupHref,
} from './_constants';

type SignupPlanCode = 'light' | 'medium' | 'heavy';

const plans = [
  {
    name: '軽量',
    nameEn: 'Lightweight',
    monthly: 980,
    annual: 9800,
    desc: 'お店・個人事業主の名刺代わりに',
    cta: 'このプランで始める',
    ctaStyle: 'lp-plan-cta-secondary',
    signupPlanCode: 'light' as SignupPlanCode,
    featured: false,
    features: [
      'サブドメイン（yourname.daigakus.com）',
      'お知らせ・メニュー・ギャラリー対応',
      'お問合せフォーム標準装備',
      'スマホ対応・SSL 自動',
      '日本語チャットサポート',
    ],
    disabled: ['独自ドメイン'],
  },
  {
    name: '中量',
    nameEn: 'Standard',
    monthly: 2890,
    annual: 28900,
    desc: '会社・店舗の正式サイトに（一番選ばれる）',
    cta: '14日間 無料で試す',
    ctaStyle: 'lp-plan-cta-primary',
    signupPlanCode: 'medium' as SignupPlanCode,
    featured: true,
    features: [
      '独自ドメイン（example.co.jp など）',
      'ページ200まで・容量20GB',
      '管理者10名でチーム運用',
      'お問合せ通知（メールで毎日）',
      '14日間 無料トライアル',
      '電話・チャットで日本語サポート',
    ],
    disabled: [],
  },
  {
    name: '重量',
    nameEn: 'Pro',
    monthly: 6890,
    annual: 68900,
    desc: '複数ブランド・大規模サイト運用',
    cta: 'このプランで始める',
    ctaStyle: 'lp-plan-cta-outline',
    signupPlanCode: 'heavy' as SignupPlanCode,
    featured: false,
    features: [
      '独自ドメイン5本',
      'ページ2,000まで・容量200GB',
      '管理者50名',
      'API / Webhook 連携',
      '監査ログ3年保持',
      '優先サポート',
    ],
    disabled: [],
  },
  {
    name: 'プラチナ',
    nameEn: 'Platinum',
    monthly: 9890,
    annual: null,
    desc: 'オーダーメイド・専属パートナー',
    cta: 'プラチナの相談をする',
    ctaStyle: 'lp-plan-cta-secondary',
    featured: false,
    features: [
      'すべて無制限',
      '専用インフラ・電話エスカレーション',
      'カスタム開発対応',
      '専属担当による継続改善',
      'SLA 99.9%',
    ],
    disabled: [],
  },
];

const faqs = [
  {
    q: 'パソコンが苦手でも大丈夫ですか？',
    a: 'はい、大丈夫です。スマートフォンで写真を投稿したり、Word のように文章を書いたりできれば、ほぼすべての操作ができます。困ったら日本語のチャットで質問できます（軽量プラン含むすべて）。',
  },
  {
    q: 'どれくらいで公開できますか？',
    a: '最短で当日中に公開できます。テンプレートを選んで、お店・会社の情報を入れて、写真をアップロードする。この3ステップで完成します。',
  },
  {
    q: '営業時間や定休日が変わったらどうしますか？',
    a: '管理画面で書き換えて「公開」を押すだけ。約5分で実際のサイトに反映されます。制作会社に依頼して数日待つ、ということはもうありません。',
  },
  {
    q: 'お問合せフォームから届いたメールは見られますか？',
    a: 'はい。お問合せがあると、登録したメールアドレスに通知が届きます。管理画面でも一覧で確認できます。中量プラン以上は毎日まとめて通知、または重量プラン以上は届いた瞬間に通知されます。',
  },
  {
    q: '途中で解約できますか？',
    a: 'いつでも解約できます。月額契約は翌月から、年契約は期末まで利用可能です。解約後もデータは一定期間保管され、いつでもエクスポート可能。「契約に縛られる」心配はありません。',
  },
  {
    q: '今あるサイトのドメイン（example.co.jp）はそのまま使えますか？',
    a: 'はい、中量プラン以上で独自ドメインがそのまま使えます。設定でつまずいたら、こちらでお手伝いします（お引越しサービス ¥30,000）。',
  },
  {
    q: '月額以外に追加費用はかかりますか？',
    a: '基本プランの月額のみで、隠れた費用はありません。ドメインを増やしたい・容量を増やしたい場合のみ、任意のアドオン（¥500 / ¥1,000）を追加できます。',
  },
  {
    q: 'サイトが急に表示されなくなる、ということはありませんか？',
    a: 'daigakus は管理画面と公開サイトを別々のサーバで動かしているので、管理画面で何かあっても、お客様が見るサイトは止まりません。中量プラン以上は SLA 99.5%（年間で止まる時間が44時間以内）をお約束しています。',
  },
];

function formatPrice(n: number) {
  return n.toLocaleString('ja-JP');
}

function monthlyFromAnnual(annual: number) {
  return Math.floor(annual / 12);
}

function signupCheckoutHref(plan: SignupPlanCode, cycle: 'monthly' | 'annual') {
  return `https://app.daigakus.com/signup/checkout?plan=${plan}&cycle=${cycle}`;
}

export default function LandingPage() {
  const [isAnnual, setIsAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileNavRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    // Move focus to first link in mobile nav
    const firstLink = mobileNavRef.current?.querySelector<HTMLElement>('a');
    firstLink?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 901) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      {/* ──── Header ──── */}
      <header className={scrolled ? 'lp-header scrolled' : 'lp-header'}>
        <div className="lp-header-inner">
          <a href="/" className="lp-logo">
            daigakus
          </a>
          <nav className="lp-nav" aria-label="グローバル">
            <a href="#how">使い方</a>
            <a href="#cases">業種別</a>
            <Link href={templatesHref}>テンプレート</Link>
            <Link href={samplesHref}>サンプル</Link>
            <a href="#pricing">料金</a>
            <a href={planListHref}>プラン詳細</a>
            <a href="#faq">FAQ</a>
            <a href={loginHref}>ログインはこちら</a>
            <a href={trialSignupHref} className="lp-nav-cta">
              無料で始める
            </a>
          </nav>
          <button
            className="lp-mobile-menu-btn"
            aria-label={mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuOpen ? 'lp-mobile-nav' : undefined}
            onClick={() => setMobileMenuOpen((o) => !o)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              {mobileMenuOpen ? (
                <>
                  <line x1="4" y1="4" x2="20" y2="20" />
                  <line x1="20" y1="4" x2="4" y2="20" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </header>
      {mobileMenuOpen && (
        <nav
          id="lp-mobile-nav"
          ref={mobileNavRef}
          className="lp-mobile-nav"
          aria-label="モバイルメニュー"
        >
          <a href="#how" onClick={() => setMobileMenuOpen(false)}>
            使い方
          </a>
          <a href="#cases" onClick={() => setMobileMenuOpen(false)}>
            業種別
          </a>
          <Link href={templatesHref} onClick={() => setMobileMenuOpen(false)}>
            テンプレート
          </Link>
          <Link href={samplesHref} onClick={() => setMobileMenuOpen(false)}>
            サンプル
          </Link>
          <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>
            料金
          </a>
          <a href={planListHref} onClick={() => setMobileMenuOpen(false)}>
            プラン詳細
          </a>
          <a href="#faq" onClick={() => setMobileMenuOpen(false)}>
            FAQ
          </a>
          <a href={loginHref} onClick={() => setMobileMenuOpen(false)}>
            ログインはこちら
          </a>
          <a href={trialSignupHref} className="lp-nav-cta" onClick={() => setMobileMenuOpen(false)}>
            無料で始める
          </a>
        </nav>
      )}

      {/* ──── Hero: 顧客の生活実感で語る ──── */}
      <section className="lp-hero">
        <div className="lp-hero-content">
          <div className="lp-hero-layout">
            <div className="lp-hero-copy">
              <div className="lp-hero-eyebrow lp-animate">
                中小企業・お店・士業のための、Web サイトサービス
              </div>
              <h1 className="lp-animate lp-animate-delay-1">
                会社の
                <br />
                ホームページ、
                <br />
                <span className="lp-hero-highlight">
                  今日始めて、
                  <br />
                  今日公開できる。
                </span>
              </h1>
              <p className="lp-hero-sub lp-animate lp-animate-delay-2">
                営業時間、写真、お知らせを自分で更新。公開サイトへすぐ反映できます。
              </p>
              <div className="lp-hero-cta-group lp-animate lp-animate-delay-3">
                <a href={trialSignupHref} className="lp-btn-primary">
                  14日間 無料で試す
                  <span aria-hidden="true">&rarr;</span>
                </a>
                <Link href={templatesHref} className="lp-btn-secondary">
                  テンプレートを見る
                </Link>
                <Link href={samplesHref} className="lp-btn-text">
                  サンプルを見る
                </Link>
              </div>
              <div className="lp-hero-trust lp-animate lp-animate-delay-3">
                <span>&#x2713; 月 ¥980〜</span>
                <span>&#x2713; 初期費用ゼロ</span>
                <span>&#x2713; いつでも解約OK</span>
              </div>
            </div>
            <div className="lp-hero-visual lp-animate lp-animate-delay-3">
              <img
                src="/lp/hero-product.webp"
                alt="daigakusの管理画面で営業時間と写真を更新し、スマートフォンの公開サイトに反映している様子"
                width={1792}
                height={1024}
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ──── Empathy: 共感ブロック ──── */}
      <section className="lp-empathy">
        <div className="lp-section-inner">
          <div className="lp-empathy-grid">
            <div className="lp-empathy-card">
              <img
                className="lp-card-image lp-card-image-left"
                src="/lp/problem-waiting.webp"
                alt=""
                width={1792}
                height={1024}
                loading="lazy"
                decoding="async"
              />
              <div className="lp-empathy-quote">
                「営業時間が変わったのに、サイトはまだ古いまま」
              </div>
              <div className="lp-empathy-detail">
                更新依頼、確認待ち、差し戻し。小さな変更ほど後回しになります。
              </div>
            </div>
            <div className="lp-empathy-card">
              <img
                className="lp-card-image lp-card-image-center"
                src="/lp/problem-cost.webp"
                alt=""
                width={1792}
                height={1024}
                loading="lazy"
                decoding="async"
              />
              <div className="lp-empathy-quote">「保守費だけで、月3万円も払っている」</div>
              <div className="lp-empathy-detail">
                維持しているだけなのに、毎月の固定費と更新費が積み上がる。
              </div>
            </div>
            <div className="lp-empathy-card">
              <img
                className="lp-card-image lp-card-image-right"
                src="/lp/problem-self-update.webp"
                alt=""
                width={1792}
                height={1024}
                loading="lazy"
                decoding="async"
              />
              <div className="lp-empathy-quote">
                「自分で更新したいけど、どこを触ればいいか分からない」
              </div>
              <div className="lp-empathy-detail">
                必要な入力欄だけが並ぶ管理画面なら、迷わず直せます。
              </div>
            </div>
          </div>
          <div className="lp-empathy-conclusion">
            <strong>daigakus は、その「面倒」を全部なくすために作りました。</strong>
          </div>
        </div>
      </section>

      {/* ──── How: 3ステップで完成 ──── */}
      <section className="lp-section" id="how">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">How it works</div>
            <h2 className="lp-section-title">3ステップで、サイトが完成する。</h2>
            <p className="lp-section-desc">
              画面の流れに沿って入力し、最後に公開ボタンを押すだけです。
            </p>
          </div>
          <div className="lp-product-flow">
            <div className="lp-section-visual lp-flow-visual">
              <img
                src="/lp/workflow-integrated.webp"
                alt="テンプレート選択、情報入力、公開完了までの3ステップ画面"
                width={1792}
                height={1024}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="lp-steps">
              <div className="lp-step-card">
                <div className="lp-step-number">01</div>
                <h3>テンプレート</h3>
                <div className="lp-step-time">1分</div>
              </div>
              <div className="lp-step-card">
                <div className="lp-step-number">02</div>
                <h3>情報入力</h3>
                <div className="lp-step-time">30分〜1時間</div>
              </div>
              <div className="lp-step-card">
                <div className="lp-step-number">03</div>
                <h3>公開</h3>
                <div className="lp-step-time">5秒</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──── What you get: 標準装備 ──── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">All-in-one</div>
            <h2 className="lp-section-title">「サイトに必要なもの」が、最初から全部入っている。</h2>
            <p className="lp-section-desc">
              プラグインを探したり、別サービスを契約したりは要りません。
            </p>
          </div>
          <div className="lp-features-grid">
            <div className="lp-feature-card">
              <div className="lp-feature-icon">SP</div>
              <h3>スマホで綺麗に表示</h3>
              <p>PC・スマホ表示を自動で最適化。</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon">Form</div>
              <h3>お問合せフォーム</h3>
              <p>通知、一覧確認、スパム対策まで標準装備。</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon">SEO</div>
              <h3>Google 検索に強い構造</h3>
              <p>速く、検索エンジンに伝わる構造。</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon">SSL</div>
              <h3>SSL（鍵マーク）も自動</h3>
              <p>設定も証明書更新も自動。</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon">Photo</div>
              <h3>写真もそのまま</h3>
              <p>アップロード後に自動で軽量化。</p>
            </div>
            <div className="lp-feature-card">
              <div className="lp-feature-icon">News</div>
              <h3>お知らせ・ブログ機能</h3>
              <p>新商品やキャンペーンをすぐ公開。</p>
            </div>
          </div>
        </div>
      </section>

      {/* ──── Use cases: 業種別 ──── */}
      <section className="lp-section" id="cases">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">Use cases</div>
            <h2 className="lp-section-title">業種に合わせて、最初から最適化。</h2>
            <p className="lp-section-desc">
              カフェにはカフェの、士業には士業の、サロンにはサロンの「いい見せ方」があります。
            </p>
          </div>
          <div className="lp-cases-grid lp-cases-grid-3-col">
            <div className="lp-case-mini">
              <img className="lp-case-thumb" src="/lp/template-cafe.webp" alt="" />
              <h4>飲食・カフェ</h4>
              <p>メニュー、営業時間、地図。</p>
            </div>
            <div className="lp-case-mini">
              <img className="lp-case-thumb" src="/lp/template-legal.webp" alt="" />
              <h4>士業・コンサル</h4>
              <p>業務内容、プロフィール、相談導線。</p>
            </div>
            <div className="lp-case-mini">
              <img className="lp-case-thumb" src="/lp/template-beauty.webp" alt="" />
              <h4>美容・サロン</h4>
              <p>メニュー、ギャラリー、予約。</p>
            </div>
            <div className="lp-case-mini">
              <img className="lp-case-thumb" src="/lp/template-medical.webp" alt="" />
              <h4>クリニック・医療</h4>
              <p>診療科目、診療時間、予約。</p>
            </div>
            <div className="lp-case-mini">
              <img className="lp-case-thumb" src="/lp/template-retail.webp" alt="" />
              <h4>小売・ショップ</h4>
              <p>商品紹介、店舗情報、問い合わせ。</p>
            </div>
            <div className="lp-case-mini">
              <img className="lp-case-thumb" src="/lp/template-corporate.webp" alt="" />
              <h4>会社・コーポレート</h4>
              <p>事業案内、実績、採用。</p>
            </div>
          </div>
        </div>
      </section>

      {/* ──── Real stories ──── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">Stories</div>
            <h2 className="lp-section-title">使った人は、こう変わった。</h2>
          </div>
          <div className="lp-stories-grid">
            <div className="lp-story-card">
              <div className="lp-story-quote">
                「営業時間の変更、これまで制作会社に頼んで3日待ってた。今は5分で自分でできる」
              </div>
              <div className="lp-story-author">
                <div className="lp-story-avatar">M</div>
                <div>
                  <div className="lp-story-name">cafe MIRAI 店主</div>
                  <div className="lp-story-meta">個人カフェ・東京 / 軽量プラン</div>
                </div>
              </div>
              <div className="lp-story-stat">
                <span className="lp-story-stat-value">¥18,000 → ¥980</span>
                <span className="lp-story-stat-label">月額（保守費を含む）</span>
              </div>
            </div>
            <div className="lp-story-card">
              <div className="lp-story-quote">
                「事務所の所員5人が交代で、お知らせを書けるようになった。WordPress
                の時は怖くて誰も触らなかった」
              </div>
              <div className="lp-story-author">
                <div className="lp-story-avatar">S</div>
                <div>
                  <div className="lp-story-name">さとう法務オフィス 事務局長</div>
                  <div className="lp-story-meta">司法書士法人・大阪 / 中量プラン</div>
                </div>
              </div>
              <div className="lp-story-stat">
                <span className="lp-story-stat-value">月¥23,000 → 月¥2,890</span>
                <span className="lp-story-stat-label">年間で約 ¥240,000 削減</span>
              </div>
            </div>
            <div className="lp-story-card">
              <div className="lp-story-quote">
                「3つのブランドサイトを別々のサービスで運用していたのを、1つにまとめた。担当者の習熟コストがゼロに」
              </div>
              <div className="lp-story-author">
                <div className="lp-story-avatar">L</div>
                <div>
                  <div className="lp-story-name">LUMIERE HOLDINGS マーケ部</div>
                  <div className="lp-story-meta">アパレル / 重量プラン</div>
                </div>
              </div>
              <div className="lp-story-stat">
                <span className="lp-story-stat-value">5サービス → 1</span>
                <span className="lp-story-stat-label">運用ツールを統合</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──── Pricing ──── */}
      <section className="lp-section" id="pricing">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">Pricing</div>
            <h2 className="lp-section-title">月額固定。隠れた費用なし。</h2>
            <p className="lp-section-desc">
              迷ったら中量プラン。8割の方がこれを選んでいます。すべて税込表示です。
            </p>
          </div>

          <div className="lp-pricing-toggle">
            <span className={!isAnnual ? 'active' : ''}>月払い</span>
            <button
              role="switch"
              aria-checked={isAnnual}
              className={isAnnual ? 'lp-toggle-switch on' : 'lp-toggle-switch'}
              onClick={() => setIsAnnual(!isAnnual)}
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                  setIsAnnual((a) => !a);
                }
              }}
              aria-label="年払い・月払い切替"
            />
            <span className={isAnnual ? 'active' : ''}>年払い</span>
            {isAnnual && <span className="lp-pricing-save">軽量〜重量プランで2ヶ月分お得</span>}
          </div>
          <div className="lp-pricing-link-row">
            <Link href={samplesHref} className="lp-pricing-plan-list-link">
              業種別の公開サンプルを見る
            </Link>
            <a href={planListHref} className="lp-pricing-plan-list-link">
              機能差分の一覧を見る
            </a>
          </div>

          <div className="lp-pricing-grid">
            {plans.map((plan) => {
              const price = isAnnual && plan.annual ? monthlyFromAnnual(plan.annual) : plan.monthly;
              const cycle = isAnnual && plan.annual !== null ? 'annual' : 'monthly';
              const planHref = plan.signupPlanCode
                ? signupCheckoutHref(plan.signupPlanCode, cycle)
                : contactHref;
              return (
                <div
                  key={plan.name}
                  className={plan.featured ? 'lp-plan-card featured' : 'lp-plan-card'}
                >
                  {plan.featured && <div className="lp-plan-badge">最も選ばれています</div>}
                  <div className="lp-plan-name">
                    {plan.name}
                    <span className="lp-plan-name-en">{plan.nameEn}</span>
                  </div>
                  <div className="lp-plan-desc">{plan.desc}</div>
                  <div className="lp-plan-price">
                    {plan.annual === null ? (
                      <>
                        <span className="price-currency">¥</span>
                        <span className="price-amount">{formatPrice(price)}</span>
                        <span className="price-period">〜/月</span>
                        <span className="price-annual">月額のみ（年払いなし）</span>
                      </>
                    ) : (
                      <>
                        <span className="price-currency">¥</span>
                        <span className="price-amount">{formatPrice(price)}</span>
                        <span className="price-period">/月</span>
                        {isAnnual && plan.annual && (
                          <span className="price-annual">
                            年額 ¥{formatPrice(plan.annual)}（税込）
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <a href={planHref} className={`lp-plan-cta ${plan.ctaStyle}`}>
                    {plan.cta}
                  </a>
                  <ul className="lp-plan-features">
                    {plan.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                    {plan.disabled.map((f) => (
                      <li key={f} className="disabled">
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="lp-pricing-guarantees">
            <span>&#x1F4B0; 7日以内なら全額返金</span>
            <span>&#x270D; いつでも解約OK</span>
            <span>&#x1F4E6; 解約後もデータ持ち出し可能</span>
            <span>&#x1F4B3; 請求書払い対応（年払い）</span>
          </div>
        </div>
      </section>

      {/* ──── Anxiety relief: 不安解消（技術的な強みはここに） ──── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">Anxiety relief</div>
            <h2 className="lp-section-title">「契約に縛られたくない」という方へ。</h2>
            <p className="lp-section-desc">公開サイト、管理画面、データを分けて守ります。</p>
          </div>
          <div className="lp-relief-composition">
            <div className="lp-section-visual lp-relief-visual">
              <img
                src="/lp/trust-modules.webp"
                alt="公開サイト、管理画面、SSL、データエクスポート、日本語サポートの安心材料"
                width={1792}
                height={1024}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="lp-relief-grid">
              <div className="lp-relief-card">
                <h3>データ持ち出し</h3>
                <p>記事も写真も、いつでもエクスポートできます。</p>
              </div>
              <div className="lp-relief-card">
                <h3>公開サイト分離</h3>
                <p>管理画面と公開サイトを分けて、表示の安定性を保ちます。</p>
              </div>
              <div className="lp-relief-card">
                <h3>日本語サポート</h3>
                <p>困ったときは専門用語なしで相談できます。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──── FAQ ──── */}
      <section className="lp-section" id="faq">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <div className="lp-section-label">FAQ</div>
            <h2 className="lp-section-title">よくあるご質問</h2>
          </div>
          <div className="lp-faq-list">
            {faqs.map((faq, i) => (
              <div key={faq.q} className="lp-faq-item">
                <button
                  id={`faq-q-${i}`}
                  className="lp-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-a-${i}`}
                >
                  <span>{faq.q}</span>
                  <span className={openFaq === i ? 'lp-faq-q-icon open' : 'lp-faq-q-icon'}>+</span>
                </button>
                <div
                  id={`faq-a-${i}`}
                  role="region"
                  aria-labelledby={`faq-q-${i}`}
                  className={openFaq === i ? 'lp-faq-a open' : 'lp-faq-a'}
                >
                  <div className="lp-faq-a-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──── Final CTA ──── */}
      <section className="lp-final-cta" id="cta">
        <div className="lp-final-visual">
          <img
            src="/lp/final-cta.webp"
            alt="サイトの公開が完了し、パソコンとスマートフォンで完成したサイトを確認している様子"
            width={1792}
            height={1024}
            loading="lazy"
            decoding="async"
          />
        </div>
        <h2>今日から始めて、今日のうちに公開しよう。</h2>
        <p>14日間無料、カード登録のみ。気に入らなければ、7日以内なら全額返金します。</p>
        <p className="lp-final-samples-hint">
          まずは作れるサイトの完成形を確認したい方は、
          <Link href={samplesHref}>業種別の公開サンプル</Link>
          もご覧ください。
        </p>
        <div className="lp-hero-cta-group">
          <a href={trialSignupHref} className="lp-btn-primary">
            14日間 無料で試す
            <span aria-hidden="true">&rarr;</span>
          </a>
          <a href={planListHref} className="lp-btn-secondary">
            料金一覧を見る
          </a>
          <a href={loginHref} className="lp-btn-text">
            ログインはこちら
          </a>
        </div>
        <div className="lp-final-trust">
          <span>&#x2713; 月 ¥980〜、初期費用ゼロ</span>
          <span>&#x2713; いつでも解約OK</span>
          <span>&#x2713; 7日以内なら全額返金</span>
          <span>&#x2713; 日本語サポート</span>
        </div>
      </section>

      <LpFooter context="lp-root" />
    </>
  );
}
